import { customElement, html } from '@adobe/edex/ui/edex-lit';

import { $t } from '@adobe/edex/ui/shared/config/i18n';
import { I18nKey } from '@adobe/edex/ui/shared/constants/i18n';

import { selectComments, selectDialog, selectShowCreateForm } from '@adobe/edex/ui/store/selectors';
import {
  deleteComment,
  fetchComments,
  reportComment,
  setCommentForm,
  unReportComment,
} from '@adobe/edex/ui/store/routines';

import {
  ConfirmationDialogParams,
  DeleteCommentDialogPayload,
  DialogState,
  EntityType,
  MarshalledComment,
} from '@adobe/edex/ui/types';
import { EdexDomID, EdexTagName, AdobeAnalyticsEvent } from '@adobe/edex/ui/shared/constants';
import './comment';
import './comment-form';
import '@adobe/edex/ui/components/shared/edex-html/edex-html';
import { BaseAuthElement } from '@adobe/edex/ui/components/base.element';
import { CommentsState } from '@adobe/edex/ui/store/reducers/api/comments';
import * as routines from '@adobe/edex/ui/store/routines';
import { delay } from '@adobe/edex/ui/shared/utils';
import { triggerOnceIfVisible } from '@adobe/edex/ui/utils';
import { Analytics } from '@adobe/edex/ui/shared/config/analytics';

import(
  /* webpackChunkName: "component/comment-list-styles" */
  /* webpackMode: "lazy" */
  './comments-list.scss'
);

@customElement(EdexTagName.commentList)
export class CommentList extends BaseAuthElement {
  static get properties() {
    return {
      ...super.properties,
      entityID: { type: String },
      comments: { attribute: false },
      dialog: { attribute: false },
      showCreateForm: { attribute: false },
    };
  }
  entityID: string;
  comments: CommentsState;
  dialog: DialogState<DeleteCommentDialogPayload>;
  showCreateForm = false;
  highlightCommentId = '';

  unsubscribe: (() => void)[];

  get useLightDom(): boolean {
    return true;
  }

  isDialogOpen(id: EdexDomID) {
    return this.dialog?.id === id;
  }

  actions = {
    fetchComments: fetchComments.trigger,
    deleteComment: deleteComment.trigger,
    unReportComment: unReportComment.trigger,
    reportComment: reportComment.trigger,
    setCommentForm: setCommentForm.trigger,
    openDialog: routines.openDialog.trigger,
    closeDialog: routines.closeDialog.trigger,
  };

  stateChanged(state) {
    super.stateChanged(state);
    this.comments = selectComments(state);
    this.showCreateForm = selectShowCreateForm(state);
    this.dialog = selectDialog(state);
  }

  async firstUpdated() {
    await this.fetchInitial();
  }

  fetchComments = () => {
    const offset = this.comments?.data?.length || 0;
    this.actions.fetchComments({
      params: {
        entityID: this.entityID,
        limit: offset ? 100 : 3,
        offset,
      },
    });
  };

  async fetchInitial() {
    if (this.comments.data) {
      return;
    }
    await delay(); // let it render first
    this.unsubscribe = triggerOnceIfVisible([this.renderRoot as HTMLElement], this.fetchComments);
  }

  disconnectedCallback() {
    this.unsubscribe?.forEach((cb) => cb());
    super.disconnectedCallback();
  }

  loadMore() {
    this.fetchComments();
  }

  handlePostComment = () => {
    const { entityID } = this;
    Analytics.getInstance().emitEvent(AdobeAnalyticsEvent.resourceComment, 'Resource - Post a comment');
    this.actions.setCommentForm({
      entityID,
      entityType: EntityType.Resource,
    });
  };

  handleDelete({ detail: comment }: CustomEvent<MarshalledComment>) {
    this.actions.openDialog({
      id: EdexDomID.commentDeleteConfirmationDialog,
      payload: { confirmCommentID: comment.id },
    });
  }

  deleteComment() {
    this.actions.deleteComment({
      id: this.dialog.payload.confirmCommentID,
    });
    this.actions.closeDialog();
  }

  handleReport({ detail: comment }: CustomEvent<MarshalledComment>) {
    if (comment.flagged) {
      this.actions.unReportComment({
        id: comment.id,
      });
    } else {
      this.actions.openDialog({
        id: EdexDomID.commentReportConfirmationDialog,
        payload: { confirmCommentID: comment.id },
      });
    }
  }

  reportComment() {
    this.actions.reportComment({
      id: this.dialog.payload.confirmCommentID,
    });
    this.actions.closeDialog();
  }

  renderConfirmationDialog({
    id,
    title,
    confirmLabel,
    subtext,
    onConfirm,
    confirmBtnVariant = 'cta',
  }: ConfirmationDialogParams) {
    if (!this.isDialogOpen(id)) {
      return null;
    }
    return html`
      <edex-dialog-next id=${id} .header=${title} ?open=${true} @close="${this.actions.closeDialog}">
        <div class="confirmation-dialog" slot="content">
          <p class="confirmation-subtext">${$t(subtext)}</p>
        </div>
        <div class="confirmation-dialog-actions" slot="footer">
          <sp-button class="cancelButton" variant="secondary" @click="${this.actions.closeDialog}">
            ${$t(I18nKey.noCancel)}
          </sp-button>
          <sp-button variant=${confirmBtnVariant} @click="${onConfirm}">${$t(confirmLabel)}</sp-button>
        </div>
      </edex-dialog-next>
    `;
  }

  renderLoader() {
    return html`
      <div class="loader">
        <sp-progress-cirlce size="xl" slot="icon" indeterminate></sp-progress-cirlce>
      </div>
    `;
  }

  renderLoadMore() {
    if (!this.comments.meta.hasMore) {
      return '';
    }
    if (this.comments.loading) {
      return this.renderLoader();
    }
    return html`
      <sp-link id=${EdexDomID.loadMoreCommentsBtn} @click="${this.loadMore}" quiet>${$t(I18nKey.loadMore)}</sp-link>
    `;
  }

  renderCreateForm() {
    if (!this.showCreateForm) {
      return '';
    }
    return html`
      <edex-comment-form></edex-comment-form>
    `;
  }

  renderCreateButton() {
    const { isGuest } = this;
    if (isGuest) {
      return '';
    }
    return html`
      <sp-link id=${EdexDomID.createCommentBtn} quiet @click=${this.handlePostComment}>
        ${$t(I18nKey.postComment)}
      </sp-link>
    `;
  }

  renderComment = (comment: MarshalledComment) => {
    return html`
      <edex-comment
        .comment=${comment}
        .highlight=${this.highlightCommentId === comment.id}
        @report=${this.handleReport}
        @delete=${this.handleDelete}
      ></edex-comment>
    `;
  };

  render() {
    const {
      comments: { meta, data, loading },
    } = this;

    if (!data) {
      return loading ? this.renderLoader() : null;
    }

    return html`
      <div class="comments-header-container">
        <div class="comments-header">
          <span class="comment-count">${$t(I18nKey.commentsCount, { count: meta.totalRecords })}</span>
          ${this.renderCreateButton()}
        </div>
        ${this.renderCreateForm()}
      </div>
      <div class="edex-comments">
        ${data.map(this.renderComment)} ${this.renderLoadMore()}
        ${this.renderConfirmationDialog({
          id: EdexDomID.commentReportConfirmationDialog,
          title: I18nKey.flagForAbuse,
          confirmLabel: I18nKey.yesFlagContent,
          subtext: I18nKey.flagContentConfirmation,
          onConfirm: this.reportComment,
        })}
        ${this.renderConfirmationDialog({
          id: EdexDomID.commentDeleteConfirmationDialog,
          title: I18nKey.deleteCommentHeader,
          confirmLabel: I18nKey.deleteCommentConfirmBtn,
          subtext: I18nKey.deleteCommentDescription,
          onConfirm: this.deleteComment,
          confirmBtnVariant: 'negative',
        })}
      </div>
    `;
  }
}
